import { createListenerMiddleware, isAnyOf, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { RootState } from '@/store'
import { ThunkExtra } from '@/store/actions'
import { selectCurrentFilterTemplate } from '@/store/beneficiary-list-filters/selectors'
import {
  addFilter,
  defaultFilterTemplate,
  removeFilter,
  reset,
  setCurrentFilterTemplate,
  setPage,
  setSortOption,
  updateFilter,
  updateSearchTextFilter,
} from '@/store/beneficiary-list-filters/slice'
import { deleteFilterTemplate, getBeneficiaries } from '@/store/beneficiary-list-filters/thunks'
import {
  assignCaseWorkerToBeneficiariesFromFilters,
  assignCaseWorkerToBeneficiary,
  unassignCaseWorkerFromBeneficiariesFromFilters,
  unassignCaseWorkerFromBeneficiary,
} from '@/store/case-workers/thunks'

const instance = createListenerMiddleware<
  RootState,
  ThunkDispatch<RootState, ThunkExtra, UnknownAction>
>()

instance.startListening({
  actionCreator: reset,
  effect: (_, listenerApi) => {
    listenerApi.cancelActiveListeners()
    listenerApi.dispatch(getBeneficiaries(defaultFilterTemplate) as unknown as UnknownAction)
  },
})

instance.startListening({
  actionCreator: setCurrentFilterTemplate,
  effect: (action, listenerApi) => {
    listenerApi.cancelActiveListeners()
    listenerApi.dispatch(getBeneficiaries(action.payload) as unknown as UnknownAction)
  },
})

instance.startListening({
  matcher: isAnyOf(setPage, addFilter, removeFilter, updateFilter, setSortOption),
  effect: (_, listenerApi) => {
    listenerApi.cancelActiveListeners()

    const currentFilterTemplate = selectCurrentFilterTemplate(listenerApi.getState())
    listenerApi.dispatch(getBeneficiaries(currentFilterTemplate) as unknown as UnknownAction)
  },
})

instance.startListening({
  actionCreator: updateSearchTextFilter,
  effect: async (_, listenerApi) => {
    listenerApi.cancelActiveListeners()

    await listenerApi.delay(500) // Debounce

    const currentFilterTemplate = selectCurrentFilterTemplate(listenerApi.getState())
    listenerApi.dispatch(getBeneficiaries(currentFilterTemplate) as unknown as UnknownAction)
  },
})

instance.startListening({
  matcher: isAnyOf(
    assignCaseWorkerToBeneficiary.fulfilled,
    unassignCaseWorkerFromBeneficiary.fulfilled,
    assignCaseWorkerToBeneficiariesFromFilters.fulfilled,
    unassignCaseWorkerFromBeneficiariesFromFilters.fulfilled,
  ),
  effect: async (_, listenerApi) => {
    listenerApi.cancelActiveListeners()
    const state = listenerApi.getState()

    if (state.beneficiary.currentBeneficiaryId) return // No need to refresh the list if we are viewing a specific beneficiary

    await listenerApi.delay(500) // Debounce

    const currentFilterTemplate = selectCurrentFilterTemplate(state)
    listenerApi.dispatch(getBeneficiaries(currentFilterTemplate) as unknown as UnknownAction)
  },
})

instance.startListening({
  predicate: (_, state, previousState) => {
    return state.beneficiaryList.scope !== previousState.beneficiaryList.scope
  },
  effect: (_, listenerApi) => {
    listenerApi.cancelActiveListeners()
    listenerApi.dispatch(reset())
  },
})

instance.startListening({
  actionCreator: deleteFilterTemplate.fulfilled,
  effect: (_, listenerApi) => {
    listenerApi.cancelActiveListeners()
    listenerApi.dispatch(reset())
  },
})

export const beneficiaryListFiltersMiddleware = instance.middleware
